/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './ExcelViewer';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { buttonSize } from 'componentregistry/registry/constants';

const ExcelViewerManager: ComponentManagerFn<'ExcelViewer'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ExcelViewer'>(platform, 'ExcelViewer'),
    type: 'ExcelViewer',
    name: 'Excel Viewer',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      attachmentId: {
        label: 'Attachment Id',
        type: 'expression',
        required: true,
        returnType: async () => 'string',
      },
      attachmentVersion: {
        label: 'Attachment Version',
        type: 'expression',
        returnType: async () => 'number',
      },
      rowsPerPage: {
        label: 'Pagination Rows Per Page',
        type: 'number',
        min: 200,
        max: 5000,
        step: 200,
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: (color: string) => {
          return ['primary', 'secondary'].includes(color);
        },
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: toOptions(['outlined', 'text']),
      },
      shape: {
        label: 'Shape',
        type: 'select',
        options: toOptions(['circular', 'rounded']),
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
      withUserInput: {
        label: 'Need input field',
        type: 'boolean',
      },
      paginationPosition: {
        label: 'Pagination Position',
        options: toOptions(['left', 'center', 'right']),
        type: 'select',
        helpText: 'Select a position of the Pagination Component.',
      },
    },
    getDisplayText: (item) => item?.props?.attachmentId?.value ?? '',
    category: 'Forms',
    icon: { iconName: 'file-excel' },
    description: `**Overview**<br/>
The Excel Viewer component allows users to preview Microsoft Excel spreadsheets (.xlsx, .xls) directly within the application interface. It renders the file contents in a paginated grid format, eliminating the need for users to download the file or open external spreadsheet software to view the data.
Use this component in document management systems, audit portals, or any workflow where verifying the contents of an uploaded spreadsheet is required before processing.

**1. File Linking**<br/>
To display a file, the component must be linked to a specific attachment stored in the system.
* **Attachment Id:** An expression resolving to the unique identifier of the stored file. (Example: \`$.selectedRow.fileId\` or \`$.query.documents.items[0].id\`).
* **Attachment Version:** (Optional) If your file storage supports versioning, you can specify a version number to view a historical copy of the file rather than the latest version.

**2. Pagination & Navigation**<br/>
Since Excel files can contain thousands of rows, this component handles large datasets via built-in pagination to maintain performance.
* **Rows Per Page:** Controls how many rows are rendered at once. (Constraints: Minimum 200, Maximum 5000).
* **Pagination Position:** Aligns the navigation controls (Next/Prev buttons) to the **Left**, **Center**, or **Right** of the viewer.
* **Need Input Field (withUserInput):** A boolean toggle. If enabled, it provides a text input allowing users to manually type a page number to jump directly to that section.

**3. UI Styling**<br/>
You can customize the appearance of the navigation controls to match your application's theme.
* **Color:** Defines the color scheme of the pagination buttons (Supported Values: **primary** or **secondary**).
* **Button Style:**
    * **Variant:** Choose between **outlined** (border only) or **text** (no border).
    * **Shape:** Choose between **circular** or **rounded** buttons.
    * **Size:** Adjusts the physical size of the controls (Small, Medium, Large).

**4. Interactivity**<br/>
While primarily a read-only viewer, the component supports basic interaction events.
* **TriggerOnClick:** You can attach a trigger to execute logic when the viewer area is clicked. This is useful for tracking views or selecting the file context.

**5. Best Use Cases**<br/>
* **Data Import Preview:** Allowing users to verify the columns and data in an uploaded sheet before clicking "Import to Database."
* **Document Archives:** Browsing historical financial reports without downloading them.
* **Approval Workflows:** Letting a manager review an expense report spreadsheet attached to a request directly in the approval screen.`,
  };
};

export default ExcelViewerManager;
